import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './CreditsCounter.css';
import AppIcon from './icons/AppIcon';

const CreditsCounter = () => {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    loadCredits();
    
    // Atualizar a cada 30 segundos
    const interval = setInterval(loadCredits, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const loadCredits = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!userData?.company_id) return;

      // Usar maybeSingle() para evitar erro quando não há registros
      const { data, error } = await supabase
        .from('company_credits')
        .select('*')
        .eq('company_id', userData.company_id)
        .gte('period_end', new Date().toISOString().split('T')[0])
        .order('period_start', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Erro ao carregar créditos:', error);
        return;
      }

      // Se não encontrou créditos válidos, pode ser que esteja aguardando renovação
      if (!data) {
        console.log('Nenhum crédito válido encontrado - aguardando renovação automática');
        setCredits(null);
        return;
      }

      setCredits(data);
    } catch (error) {
      console.error('Erro ao carregar créditos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="credits-counter loading">
        <AppIcon name="hourglass_empty" />
      </div>
    );
  }

  if (!credits) {
    return null;
  }

  const usagePercent = (credits.credits_used / credits.credits_total) * 100;
  const isLow = usagePercent >= 80;
  const isCritical = usagePercent >= 90;

  return (
    <div 
      className={`credits-counter ${isLow ? 'low' : ''} ${isCritical ? 'critical' : ''}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <AppIcon name="account_balance_wallet" />
      <span className="credits-amount">
        {credits.credits_available.toLocaleString('pt-BR')}
      </span>
      
      {showTooltip && (
        <div className="credits-tooltip">
          <div className="tooltip-header">
            <AppIcon name="account_balance_wallet" />
            <strong>Créditos</strong>
          </div>
          
          <div className="tooltip-stats">
            <div className="stat-row">
              <span>Disponíveis:</span>
              <strong>{credits.credits_available.toLocaleString('pt-BR')}</strong>
            </div>
            <div className="stat-row">
              <span>Usados:</span>
              <strong>{credits.credits_used.toLocaleString('pt-BR')}</strong>
            </div>
            <div className="stat-row">
              <span>Total:</span>
              <strong>{credits.credits_total.toLocaleString('pt-BR')}</strong>
            </div>
            {credits.credits_bonus > 0 && (
              <div className="stat-row bonus">
                <span>Bônus:</span>
                <strong>+{credits.credits_bonus.toLocaleString('pt-BR')}</strong>
              </div>
            )}
          </div>
          
          <div className="tooltip-progress">
            <div className="progress-bar">
              <div 
                className={`progress-fill ${isLow ? 'low' : ''} ${isCritical ? 'critical' : ''}`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
            <span className="progress-text">{usagePercent.toFixed(1)}% usado</span>
          </div>
          
          <div className="tooltip-period">
            <AppIcon name="calendar_today" />
            <span>
              Renova em: {new Date(credits.period_end).toLocaleDateString('pt-BR')}
            </span>
          </div>
          
          {isLow && (
            <div className={`tooltip-alert ${isCritical ? 'critical' : ''}`}>
              <AppIcon name={isCritical ? 'error' : 'warning'} />
              <span>
                {isCritical 
                  ? 'Créditos quase esgotados!' 
                  : 'Atenção: créditos baixos'}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CreditsCounter;
